// Guide Block Types:
// - "section": { id: string, title: string, content: []}
// - "subsection": { title: string, content: [] }
// - "subdivision": { title: string, content: [] }
// - "text": { data: string | () => JSX }
// - "image": { data: string (url) }
// - "gallery": { images: [{ src, alt, credit: { name, url } }] }
// - "table": { data: [ {heading: row}]}
// - "list": { data: string (newline separated list) }
// - "component": { data: string (key to componentMap) }
// - "inlineLink": { text: string, onClick: fn }

const starterGuideData = {
  id: "starter-guide",
  title: "Starter Guide",
  category: "Guides",
  slug: "beginner",
  meta: {
    description: "A comprehensive guide to get started with Pixels and basic, compiled information from the Pixels team",
    keywords: ["beginner", "tutorial", "starter", "game"],
    author: "PixeLore",
    date: "2025-03-29",
  },
  sections: [
    {
      id: "about-pixels",
      title: "About Pixels",
      type: "section",
      content: [
        {
          type: "subdivision",
          id: "brief-history",
          title: "Brief History",
          content: [
            {
              type: "text",
              data: `
              Pixels started in 2020 as <i>Banger.Digital</i>, aiming to host virtual events - a blend of Club Penguin and Zoom.<br>
              In early 2021, the team joined <i>Mesh</i> to build an online events platform, which was later sunset. This led to the creation of <b>Pixels</b> - a farming MMORPG inspired by Stardew Valley and Runescape.<br>
              On Jan 31, 2022, Pixels launched its first <b>Play-to-Mint</b>, where players grinded to mint Land NFTs for 0.06 ETH (~$160).<br>
              In August 2022, Pixels launched the <b>Play-to-Airdrop (P2A)</b> event, running for 2 months. It attracted hundreds of thousands of players and became one of the largest P2A campaigns in Web3 history.<br>
              On Nov 16, 2022, <b>Chapter 1</b> launched, officially starting the Pixels game. All previous progression reset, and the in-game token <b>$BERRY</b> was introduced.<br>
              In November 2023, Pixels moved to the <b>Ronin</b> chain, launched its second NFT, and held the popular <b>Carnival event</b>.<br>
              Shortly after the move on-chain, Pixels removed the General Store for sales and introduced the <b>Task Board</b>.<br>
              In Jan 2024, <b>P2A Part 2</b> began, leading to the highly anticipated release of <b>$PIXEL</b>. In Feb 2024, $BERRY was sunset, and <b>Coins</b> and <b>$PIXEL</b> officially launched.<br>
              In July 2024, <b>Chapter 2</b> released, bringing major economic changes and sparking community debate. <b>Chapter 2.5</b> followed in late 2024 into early 2025.<br>
              In March 2025, another game-changing feature was added called <b>Merchant Fleet</b>.<br> 
              <b>Staking</b> was welcomed to the Ecosystem on May 1st 2025.<br> 
              Despite challenges, one thing remains constant: the Pixels team never stopped building.
              `,
            },
            {
              type: "gallery",
              images: [
                {
                  src: "https://Pixelore.s3.eu-central-003.backblazeb2.com/Old+Pixels/%7B888C0A19-92E8-4AAF-A179-51772FDE3434%7D.png",
                  alt: "Original Terra Villa",
                  credit: {
                    name: "Hath",
                    url: "https://www.youtube.com/@0xHath",
                  },
                },
                {
                  src: "https://Pixelore.s3.eu-central-003.backblazeb2.com/Old+Pixels/%7B7AF533B4-483E-4283-A631-549AF4E35514%7D.png",
                  alt: "Original Cooking",
                  credit: {
                    name: "Hath",
                    url: "https://www.youtube.com/@0xHath",
                  },
                },
                {
                  src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Old+Pixels/image_2025-03-29_152329511.png",
                  alt: "Original Shopping",
                  credit: {
                    name: "Hath",
                    url: "https://www.youtube.com/@0xHath",
                  },
                },
              ],
            },
          ],
        },
        {
          type: "subdivision",
          id: "what-is-pixels",
          title: "What is Pixels Right Now?",
          content: [
            {
              type: "text",
              data: "Pixels is moving towards being a producer in the Web3 Market. Their goal appears to be to host multiple games all within the Pixels Ecosystem, while trying to solve P2E.",
            },
          ],
        },
        {
          type: "subdivision",
          id: "what-is-pixels-game",
          title: "What is Pixels (The Game)?",
          content: [
            {
              type: "text",
              data: "Pixels is a captivating Web3 Play-to-Earn and Play-to-Own MMO where you can grind, explore, complete missions or socialize in an open-world universe. The main currencies used within the game are Coins (off-chain) and $PIXEL (on-chain). <br/><br/>It is completely free-to-play and requires nothing from you except for you to create an account. Everything else is <b>optional</b>",
            },
          ],
        },
        {
          type: "subdivision",
          id: "who-is-founder",
          title: "Who is the founder? And who are the faces of Pixels?",
          content: [
            {
              type: "text",
              data: "Luke Barwikowski is the founder of Pixels, and along with Heidi, they are the faces of Pixels.",
            },
          ],
        },
        {
          type: "subdivision",
          id: "is-this-for-me",
          title: "Is this game for me?",
          content: [
            {
              type: "text",
              data: "That depends on your likes and dislikes. Is your only objective to make money and not really have fun? Then this is probably not the game for you.<br/><br/>Are you here to make friends and relax? Do you enjoy social and repetitive tasks? Are you occasionally a fellow grinder who likes competition? Pixels offers all of these things. And continues to build out to so much more. Pixels has a lot to offer as a social game, and with their continued growth & success, it can only become much more.",
            },
          ],
        },
        {
          type: "subdivision",
          id: "nfts",
          title: "What NFTs are in the game?",
          content: [
            {
              type: "text",
              data: "Pixels has two NFTs. A Land NFT (find more information about them <a href='../../wiki/lands' rel='noreferrer' target='_blank' class='wiki-links'>here</a>) and a Pet NFT (find more information about them <a href='../../wiki/pets' rel='noreferrer' target='_blank' class='wiki-links'>here</a>). There are also many external NFTs integrated into the game for use as Avatars. Check them out on the Avatars Page <a href='/avatars/'>here.</a>",
            },
          ],
        },
        {
          type: "subdivision",
          id: "how-to-earn",
          title: "How do I earn & how much can I earn?",
          content: [
            {
              type: "text",
              data: "This isn't a question I really like answering because this is still a game and that's my purpose as a player! But for the sake of information... Players can currently earn via selling Coins or $PIXEL that they make in the game. You can make coins and $PIXEL by completing tasks on the Task Board which is located in your house. Some players sell coins for FIAT or $PIXEL (this can't be done in the Pixels Discord) but it's really at your own risk... And if you have enough reputation to withdraw, $PIXEL can be withdrawn from the dashboard and sold. You might not make a lot for the day. But over time, you can build up an account that might earn way more than a new player! The price of $PIXEL fluctuates as well, so it's all dependent on current market conditions. If you look at the monthly distribution charts, Pixels puts 27.86M $PIXEL into Ecosystem Rewards monthly. If you divide that over the course of the month, you can see the base amount of $PIXEL that should be delivered daily.",
            },
          ],
        },
        {
          type: "subdivision",
          id: "airdrop",
          title: "Is there an airdrop?",
          content: [
            {
              type: "text",
              data: "Not really! The founder considers everyday at Pixels an airdrop, but there aren't any plans for more Airdrop events in the future. <br/>The focus now is on major events and competitions, and continual work on making the game fulfilling and entertaining!",
            },
          ],
        },
        {
          type: "subdivision",
          id: "team-size",
          title: "How big is the Pixels team?",
          content: [
            {
              type: "text",
              data: "Based on info from the AMA, Luke & Heidi have claimed that the Pixels team was around 30 members. It used to be around 15 members (art, marketing, design, devs, community team) in the beginning.",
            },
          ],
        },
        {
          type: "subdivision",
          id: "can-everyone-play",
          title: "Can my entire family play?",
          content: [
            {
              type: "text",
              data: "Yup! But there are some people who try to abuse the game and sign in on multiple accounts ( •̀ - •́ ) We don't like those people",
            },
          ],
        },
        {
          type: "subdivision",
          id: "i-have-questions",
          title: "I have more questions!!",
          content: [
            {
              type: "text",
              data: "I'm only one person (,,>﹏<,,) But I am interested in helping as many as I can! Use the 'Contact Me' button in the bottom right.<br/>But please don't contact me about your bans! I am not on the Pixels team, and I cannot do anything about it.. I know it's frustrating, but I don't want my inbox flooded (っ °Д °;)っ",
            },
          ],
        },
      ],
    },
    {
      id: "basics",
      title: "Basics",
      type: "section",
      content: [
        {
          type: "subsection",
          id: "movement",
          title: "Movement",
          content: [
            {
              type: "subdivision",
              id: "walking",
              title: "Regular walking or Movement",
              content: [
                {
                  type: "text",
                  data: "You can use your mouse, your arrow keys or WASD on your keyboard to move around.",
                },
                {
                  type: "text",
                  data: "For players who don't have a QWERTY keyboard, there's no way to remap the buttons within the game ( •̯́ ^ •̯̀)",
                },
              ],
            },
            {
              type: "subdivision",
              id: "mobile-walking",
              title: "Mobile Movement",
              content: [
                {
                  type: "text",
                  data: "Mobile players can use the joystick or can just hold down on a part of the map to move.",
                },
                {
                  type: "text",
                  data: "The joystick on mobile can be enabled or disabled depending on your preference. Just click the <img src='https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/Icons/SettingsButton.png' alt='Settings Icon' class='inline w-3 h-3'/> Settings Button in the top right.",
                },
                {
                  type: "image",
                  data: {
                    img: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/mobilesettings.jpg",
                    alt: "Mobile Settings",
                  },
                },
              ],
            },

            {
              type: "subdivision",
              id: "keybinds",
              title: "Keybinds",
              content: [
                {
                  type: "text",
                  data: `PC users:<br/>
                          B opens your bag<br/>
                          C opens chat<br/>
                          Z zooms the map<br/>
                          ESC closes most dialogs or deselects items<br/>
                          TAB cycles through your bag<br/>
                          1-9 selects the first 9 items from your Bag<br/>
                          Ctrl+H hides all players from the map for 30 seconds<br/>`,
                },
                {
                  type: "text",
                  data: `You can map bag items to specific keys on your keyboard (as long as they're not one of the above mentioned keys).<br/>
                Hold down the button on your keyboard that you want to map, then select the item you want to map it to. Now whenever you click that button, the item will be selected from your bag!<br/>
                To re-use the button, just hold down the button again and click a different item. <br/>
                To clear all of your keybindings, go to <img src='https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/Icons/SettingsButton.png' alt='Settings Icon' class='inline w-3 h-3'/> Settings, then click the "Clear Shortcuts" button. (You'll only see this button on a non-mobile device)<br/>`,
                },
                {
                  type: "image",
                  data: {
                    img: `https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_163431953.png`,
                    alt: "Desktop Settings",
                  },
                },
              ],
            },
            {
              type: "subdivision",
              id: "other-movement",
              title: "Other Info",
              content: [
                {
                  type: "text",
                  data: `You can't walk while zoomed in sadly.`,
                },
              ],
            },
          ],
        },
        {
          type: "subsection",
          id: "avatar-creation",
          title: "Avatar Creation",
          content: [
            {
              type: "subdivision",
              title: "Avatar Data",
              content: [
                {
                  type: "text",
                  data: "Click on the <img src='https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/Icons/Player.png' alt='Player Icon' class='inline w-5 h-5 '/> Player Icon and select 'Change Avatar'. From this menu, you can select an array of clothing, skin colors, facial styles, and accessories. If you purchased clothing and placed them in your wardrobe, they're all accessible from this menu! Kinda cool, if you ask me.",
                },
                {
                  type: "text",
                  data: "One other option for avatars is integrated NFTs! If you own an NFT that has been integrated by Pixels, you can use that NFT in-game as an avatar. From the same Player Menu, select Choose NFT Avatar and pick from the selection you have available. There are so many cool ones to choose from.",
                },
                {
                  type: "text",
                  data: "See a full list of integrated avatars <a href='../../Avatars/' rel='noreferrer' target='_blank' class='wiki-links'>here.</a>",
                },
              ],
            },
          ],
        },
        {
          type: "subsection",
          id: "moving-between-lands",
          title: "Moving Between Lands",
          content: [
            {
              type: "subdivision",
              id: "avatar-data",
              title: "Moving Between Lands",
              content: [
                {
                  type: "text",
                  data: "There are three ways to get around to Pixel Lands. Well, four. But that fourth one is so tedious.",
                },
                {
                  type: "text",
                  data: `<b>The First Way</b>: Using the Infiniportal. The Infiniportal is the method to traverse to specific lands quickly and easily. <br/>
              Click on the Floating Land bottom left of the Fountain, and you can choose to search by land Number or by a specific player to quickly go to those lands! ╰(*°▽°*)╯`,
                },
                {
                  type: "gallery",
                  images: [
                    {
                      src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/infiniportal.png",
                      alt: "Infiniportal",
                    },
                    {
                      src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_164625367.png",
                      alt: "Infiniportal search by Number",
                    },
                    {
                      src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_164640925.png",
                      alt: "Infiniportal Search By User",
                    },
                  ],
                },
                {
                  type: "text",
                  data: `<b>The Second Way</b>: Using Bookmarks! You get 3 Bookmarks when you start that you can use on regular lands. After 3, you can only acquire more by getting VIP or buying NFT Land.<br/>
                     To set a bookmark, you have to use method 1 first! Visit a land, and if you like that land, click on the Gate. <br/>
                     From there, you should be able to bookmark. (Look at the picture below and see the part with the gate that has the number on it? Yeah that's the one.)<br/>
                     Then to access the bookmark, click the <img src='https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/Icons/Land.png' alt='Land Icon' class='inline w-3 h-3'/> Land Icon top left, and your bookmarks will be there! Easy access.`,
                },
                {
                  type: "gallery",
                  images: [
                    {
                      src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_180005540.png",
                      alt: "Land Gate",
                    },
                    {
                      src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_175955457.png",
                      alt: "Bookmark",
                    },
                    {
                      src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_180128743.png",
                      alt: "Bookmarks Tab",
                    },
                  ],
                },
                {
                  type: "inlineLink",
                  data: {
                    text: "Infiniportables",
                    itemId: "itm_infiniportable",
                    description: `<b>The Third Way</b>: Infiniportables! They're like the Infiniportal, 
                              but they are an inventory item and you can take them with you. 
                              They're single-use though, so if you don't have any, you're out of luck. 
                              You can get them from the Pixel HQ for 1 $PIXEL, or wait for an event to craft them.`,
                  },
                },
                {
                  type: "text",
                  data: `<b>The Fourth Way</b>, and the least feasible way: Walk along the Rainbow Road! That shiny multi-colored pathway that you see every time you visit Terra Villa? It connects all 5000 lands together! You can walk left or right, and meet more farm lands. But it sure is tiring to walk.`,
                },
                {
                  type: "image",
                  data: {
                    img: `https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_183659015.png`,
                    alt: "The Road",
                  },
                },
              ],
            },
          ],
        },
        {
          type: "subsection",
          id: "trading",
          title: "Trading",
          content: [
            {
              type: "subdivision",
              id: "trading-data",
              title: "Trading",
              content: [
                {
                  type: "text",
                  data: `<strong>Trading</strong> is one of the simplest ways to get products and coins between players. You can trade items or coins to another account easily, by clicking on them and selecting "Trade".`,
                },
                {
                  type: "text",
                  data: "It's really easy and secure. The trade only completes when you've both locked in, and if anyone makes any changes, you need to Confirm again to make sure you agree with the changes! Always be diligent and keep an eye on what the other player is trading. While I love trusting people, people aren't always trustworthy.",
                },
                {
                  type: "text",
                  data: `And while trading is great, it is still very limited if you're a F2P player or if you haven't acquired enough reputation. Check out <a href="./reputation" target="_blank" rel="noreferrer" class='wiki-links'>Reputation</a> to see the updated rep values. But if you haven't hit the second threshold, you can only trade up to 2000 coins unbalanced, and everything else must be either balanced, or you cannot trade.`,
                },
                {
                  type: "text",
                  data: "So what is a Balanced Trade? It means the Trade Value in the trade window match each other. So every item you add to the trade increases the trade value. When your value and the other player's value matches, it is balanced.",
                },
                {
                  type: "image",
                  data: {
                    img: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_184131324.png",
                    alt: "Balanced Trade",
                  },
                },
                {
                  type: "text",
                  data: `The Marketplace is another great way to trade your materials for coins, or to buy something that you might need!<br/>
                    The marketplace is public and definitely helps if you don't feel like trading direct with players.<br/>
                    It is located inside of the Buck's Galore, which is the public store to the right of the Fountain.<br/>
                    Go to the back room, and you'll see a Buy and Sell station where you can list up to 5 items as a F2P player. You can list more if you have VIP or Land Ownership!(≧◡≦)`,
                },
                {
                  type: "text",
                  data: `Oh and if you add things to the MP as VIP or Land Owner, then lose those statuses, the items wont be removed from the MP! They'll stay but if you're over your limit, you can't add anymore.`,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "vip",
      title: "VIP",
      type: "section",
      content: [
        {
          type: "subdivision",
          id: "what-is-vip",
          title: "What is VIP?",
          content: [
            {
              type: "text",
              data: `VIP is a great way to put $PIXEL back into the ecosystem plus earn some extra benefits. It gives you some goodies in exchange for some $PIXEL. And as of July 15th 2025, there are now VIP Tiers!<br/>
              But to better answer this question, VIP is just a membership card in Pixels for better in-game QoL bonuses`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "tiering",
          title: "VIP Tiers",
          content: [
            {
              type: "text",
              data: `             
                 On June 1st, 2026, the Pixels Team changed VIP Tiering. Two new tiers were added, points were changed, benefits were changed and more. I've updated as much as I can for the new information!\n
                `,
            },
            {
              type: "text",
              data: `             
                 On July 15th, 2025, the Pixels team added VIP Tiering to the game. With this came more benefits and more ways to upgrade your gaming experience.<br/><br/>
                <b>How do you get the higher tiers?</b> When you first purchase VIP (or if you're a Land Owner and get VIP naturally), you start at Tier 1. Every user begins at Tier 1.<br/>
                As you spend $PIXEL, you increase your points towards a higher tier (that's the only way to go up a tier!).
                `,
            },
            {
              type: "text",
              data: `<b>So how much points do you need?</b> Here are some estimates based on community-findings!`,
            },
            {
              type: "table",
              data: [
                {
                  tier: 1,
                  $PIXEL: 0,
                },
                {
                  tier: 2,
                  $PIXEL: "2,500",
                },
                {
                  tier: 3,
                  $PIXEL: "1,000 (3,500 Total)",
                },
                {
                  tier: 4,
                  $PIXEL: "700 (4,200 Total)",
                },
                {
                  tier: 5,
                  $PIXEL: "700 (4,900 Total)",
                },
                {
                  tier: 6,
                  $PIXEL: "500 (5,400 Total)",
                },
              ],
            },
            {
              type: "text",
              data: `It's worth knowing that if you're coming from NO VIP, and you use anything other than a 1-month, 1-week or 3-day VIP Coupon, you'll gain points to jump to your correct tier!`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "benefits-of-vip",
          title: "What are the benefits of VIP?",
          content: [
            {
              type: "table",
              data: [
                {
                  Tier: "<b>Base (No VIP)</b>",
                  Bookmarks: "3",
                  "Inventory Space": "30",
                  Reputation: "Varies",
                  "Time for VIP Lounge": "None",
                  "Marketplace Listings": 5,
                  "Stack Limits": "x99",
                  "Infinifunnel": "No",
                  "Surplus Energy": "0",
                  "Wardrobe Space": "36",
                },
                {
                  Tier: "<b>1</b>",
                  Bookmarks: "+3 (6 Total) (33 if LO)",
                  "Inventory Space": "+0 (30 total) (42 if LO)",
                  Reputation: "+600 (600k Total from VIP)",
                  "Time for VIP Lounge": "24h",
                  "Marketplace Listings": "+5 (10 Total) (+25 if LO)",
                  "Stack Limits": "x99",
                  "Infinifunnel": "Yes, 10 Energy/Task",
                  "Surplus Energy": "0",
                  "Wardrobe Space": "36",
                },
                {
                  Tier: "<b>2</b>",
                  Bookmarks: "+9 (12 Total) (39 if LO)",
                  "Inventory Space": "+6 (36 total) (48 if LO)",
                  Reputation: "+1500 (2.1k Total from VIP)",
                  "Time for VIP Lounge": "12h",
                  "Marketplace Listings": "+10 (15 Total) (30 if LO)",
                  "Stack Limits": "x199",
                  "Infinifunnel": "Yes, 4 Energy/Task",
                  "Surplus Energy": "0", 
                  "Wardrobe Space": "36",
                },{
                  Tier: "<b>3</b>",
                  Bookmarks: "+27 (30 Total) (57 if LO)",
                  "Inventory Space": "+12 (42 total) (54 if LO)",
                  Reputation: "+1500 (3.6k Total from VIP)",
                  "Time for VIP Lounge": "8h",
                  "Marketplace Listings": "+15 (20 Total) (35 if LO)",
                  "Stack Limits": "x299",
                  "Infinifunnel": "Yes, 4 Energy/Task",
                  "Surplus Energy": "400", 
                  "Wardrobe Space": "54",
                },{
                  Tier: "<b>4</b>",
                  Bookmarks: "+45 (48 Total) (75 if LO)",
                  "Inventory Space": "+18 (48 total) (60 if LO)",
                  Reputation: "+2,000 (5.6k Total from VIP)",
                  "Time for VIP Lounge": "6h",
                  "Marketplace Listings": "+55 (60 Total) (75 if LO)",
                  "Stack Limits": "x499",
                  "Infinifunnel": "Yes, 2 Energy/Task",
                  "Surplus Energy": "700", 
                  "Wardrobe Space": "72",
                },{
                  Tier: "<b>5</b>",
                  Bookmarks: "+96 (99 Total) (126 if LO)",
                  "Inventory Space": "+24 (54 total) (66 if LO)",
                  Reputation: "+3500 (9.1k Total from VIP)",
                  "Time for VIP Lounge": "4h",
                  "Marketplace Listings": "+95 (100 Total) (115 if LO)",
                  "Stack Limits": "x699",
                  "Infinifunnel": "Yes, 1 Energy/Task",   
                  "Surplus Energy": "1200",                
                  "Wardrobe Space": "108",
                },{
                  Tier: "<b>6</b>",
                  Bookmarks: "+119 (124 Total) (151 if LO)",
                  "Inventory Space": "+30 (60 total) (72 if LO)",
                  Reputation: "+4000 (13.1k Total from VIP)",
                  "Time for VIP Lounge": "4h",
                  "Marketplace Listings": "+145 (150 Total) (165 if LO)",
                  "Stack Limits": "x999",
                  "Infinifunnel": "Yes, 0.5 Energy/Task",
                  "Surplus Energy": "2000", 
                  "Wardrobe Space": "144",
                },
              ],
            },
          ],
        },
        {
          type: "subdivision",
          id: "cost-of-vip",
          title: "How much does it cost?",
          content: [
            {
              type: "text",
              data: `The base VIP costs about $15. The price resets everyday at midnight UTC to match USD Price\n
              If you buy longer duration, the price decreases in USD per month.\n
              Getting to higher tiers varies in cost depending on your tier, and how quickly it decays.`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "where-to-get-vip",
          title: "Where do I get it?",
          content: [
            {
              type: "text",
              data: `There's a fancy VIP booth in Terra Villa just southwest of the hot air balloon. You can get a 1-month, 3-month, 6-month or 12-month VIP Pass. And remember you have to use it on yourself by clicking it in your bag, and then clicking on yourself!
                       `,
            },
            {
              type: "text",
              data: `You cannot sell or trade any VIP coupons except for the 12-month one.ヾ( ˃ᴗ˂ )◞ • *✰`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "vip-expiration",
          title: "What happens when the VIP ends?",
          content: [
            {
              type: "text",
              data: `Well! You lose your VIP Benefits.`
            },
            {
              type: "list",
              data: `Bag space reduces and the items get sent to your mailbox.
              Your bookmarks will stay unless you remove them!
              Your stacks wont split apart but once separated, they wont stack anymore.
              You will lose access to the VIP Sauna
              Your marketplace listings wont be removed, but you cant add anymore until you're back under the limit
              You also lose the Reputation points that you gained from VIP.
              If you lose a VIP tier, the same thing applies, but not as much. You'll lose the benefits of that tier going forward, but the only thing that'll happen to your account immediately is the loss of reputation and bag space!`,
            },
          ],
        },
      ],
    },
    {
      id: "reputation",
      title: "Reputation",
      type: "section",
      content: [
        {
          type: "subdivision",
          id: "what-is-rep",
          title: "What is Reputation?",
          content: [
            {
              type: "text",
              data: `Every account has reputation (also known as trust score). Reputation is something the team put in place on accounts to reward good players.<br/>
                    Players must build up their reputation via various tasks to be able to do things like trade, access the marketplace or withdraw.<br/>
                    You also get benefits like lower marketplace fees, and lower withdrawal tax!<br/>
                    Having a higher reputation usually means you're an active and good player. However the team's implementation of Reputation has not been the best.<br/>
                    Easily bottable quests and events caused reputation to not be so trustworthy, and has put reputation in a somewhat crappy spot.<br/>
                    If you try to do something and see a 'low-trust-score' warning, it's because you don't have enough reputation`,
            },
            {
              type: "text",
              data: `Check your reputation (trust score) on the dashboard:
              <img src="https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-04-21_185503192.png" alt="Rep screen" class="w-48"/>
              You can also get a general idea of the breakdown of your reputation on your profile in <a href="../../Lookup/Players" rel="noreferrer" target="_blank" class='wiki-links'>Player Lookups</a>.`,
            },
            {
              type: "text",
              data: `<span class="font-heading3 font-bold">Reputation Limits:</span><br/>
                     🪶 Withdraw Access: 2,000 Reputation<br/>
                     🪶 Marketplace Buy: 600 Reputation<br/>
                    🪶 Marketplace Sell: 1,100 Reputation<br/>
                    🪶 Trading Access: <br/>
                    🪶🪶 800 Reputation: 2,000 Value Unbalanced. Balanced Trading After 2,000 Value<br/>
                    🪶🪶 1,500 Reputation: 30,000 Value Unbalanced. Balanced Trading After 30,000 Value<br/>
                    🪶🪶 2,000 Reputation: Coin Trading Enabled.<br/>
                    🪶🪶 2,500 Reputation: 100,000 Coins Unbalanced. Balanced Trading after 30,000 Value<br/>
                    🪶🪶 3,000 Reputation: Up to 200,000 Coins Unbalanced. Balanced Trading after 100,000 Value.<br/>
                    🪶🪶 4,000 Reputation: Unlimited Unbalanced Trading.<br/>
                    🪶 Guild Creation Access: 2205 Reputation <br/><br/>
                    🪶 Join Guild: 200 Reputation <br/><br/>
                    ⚠️ Unbalanced Trading: You lose reputation for unbalanced trading (if you do it a lot)<br/>
                    ⚠️ Restriction: -3000 Reputation`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "trade-fees",
          title: "Reputation Marketplace/Trade Fees",
          content: [
            {
              type: "text",
              data: `I am recording this as I discover the information -- If you know more info, DM me on Twitter!<br/>
                    This fee is charged on sale or trade, and is determined by the amount being spent + reputation of the player.<br/>
                    💹 To explain it more simply, if you sell an item on the Marketplace, you are charged a % of the amount as a fee.<br/>
                    🫱🏽‍🫲🏽If you trade coins, a % of the coins will be deducted based on the receiver's fee %.<br/>
                    Here is a table of data I have collected so far based on reputation to fees. It might just be an estimate for now so not fully accurate!`,
            },
            {
              type: "table",
              data: [
                {
                  tier: 1,
                  reputation: "0 - 1499 (also banned/restricted players)",
                  fee: "30%",
                },
                {
                  tier: 2,
                  reputation: "1500 - 2000",
                  fee: "18% - 15%",
                },
                {
                  tier: 3,
                  reputation: "2001 - 4000",
                  fee: "15% - 9%",
                },
                {
                  tier: 4,
                  reputation: "4001 - 4800",
                  fee: "9% - 6%",
                },
                {
                  tier: 5,
                  reputation: "4801 - 5500",
                  fee: "6% - 5%",
                },
                {
                  tier: 6,
                  reputation: "5501 - 5900",
                  fee: "5% - 4%",
                },
                {
                  tier: 7,
                  reputation: "5901 - 6200",
                  fee: "4% - 3%",
                },
                {
                  tier: 8,
                  reputation: "6201 - 6400",
                  fee: "3% - 2%",
                },
                {
                  tier: 9,
                  reputation: "6401 - 6600",
                  fee: "2% - 1%",
                },
                {
                  tier: 10,
                  reputation: "6601+",
                  fee: "1%",
                },
              ],
            },
          ],
        },
        {
          type: "subdivision",
          id: "how-to-earn-rep",
          title: "How do I earn Reputation?",
          content: [
            {
              type: "text",
              data: `We don't have a breakdown of the new reputation scores coming out, or how they are earned.<br/>
              I am going to do my best to keep my eyes peeled for this data, but for now this is what we know:<br/><br/>
              🪶 VIP: <br/>
              →→→Tier 1: 1500<br/>
              →→→Tier 2: 2000<br/>              
              →→→Tier 3: 3000<br/>
              →→→Tier 4: 4500<br/>
              🪶 NFT Land Owner: 5,000 for 1 Land, 500 for each additional land. Stacks up to 60 Lands (Max: 30,000)<br/>
              🪶 Connecting Twitter: 50<br/>
              🪶 Connecting Discord: 100<br/>
              🪶 Connecting Phone: 50<br/>
              🪶 Having a Guild Shard: 30<br/>
              🪶 Connecting Email: 5<br/><br/>

              🪶 Account Age: log10(Account Age in Days x 0.15)/40<br/>
              🪶 Owning Pet NFT (or Multiple): 30 (15 Points Per Pet, Up to 105 - 6 pets)<br/>
              🪶 Purchasing Quicksilver Pack 1: 5 Reputation (up to 100) (20x)<br/>
              🪶 Purchasing Quicksilver Pack 2: 12 Reputation (up to 240) (20x)<br/>
              🪶 Purchasing Quicksilver Pack 3: 80 Reputation (up to 1600) (20x)<br/>
              🪶 Transactions<br/><br/>              
              🪶 Owning Integrated NFTs (see points below)<br/>
              🪶 Participating in Live Ops Events & Quests (See Below for a list of ongoing and Past Reputation Quests)<br/>
              ⚠️ Withdrawals and Deposits Affect Reputation`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "rep-quests",
          title: "Reputation Quests",
          content: [
            {
              type: "component",
              data: `ReputationTasks`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "avatar-rep",
          title: "Avatar Reputation",
          content: [
            {
              type: "component",
              data: "AvatarRep",
            },
          ],
        },
      ],
    },
    {
      id: "inventory-dailies",
      title: "Inventory & Dailies",
      type: "section",
      content: [
        {
          type: "subdivision",
          id: "inventory-and-storage",
          title: "Inventory & Storage",
          content: [
            {
              type: "text",
              data: `Every player starts with 30 slots of inventory. You can add on 6 by getting VIP and another 6 if you're a land owner. <br/>
              Storage boxes can be crafted using the Woodworking Skill, so that you can have your own personal storage boxes on your speck or lands! Also, if you purchase clothes, or avatar accessories, you can store them in your wardrobe inside the house.`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "free-storage",
          title: "Free Storage",
          content: [
            {
              type: "text",
              data: `<h2 class="font-ubuntu underline font-semibold">Public Storage #1</h2>
              Inside the Post Office has one chest where you can store 12 items!`,
            },
            {
              type: "text",
              data: `<h2 class="font-ubuntu underline font-semibold">Public Storage #2</h2>
              Inside the Drunken Goose, there's another 12-slot storage chest for keeping more things!`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "dailies",
          title: "Dailies",
          content: [
            {
              type: "text",
              data: `<h2 class="font-ubuntu underline font-semibold">Daily #1</h2>
              You can get daily login coins from the Dashboard every day!`,
            },
            {
              type: "text",
              data: `
              <h2 class="font-ubuntu underline font-semibold">Daily #2</h2>
              The Post Office has a special package for you every 24 hours with a possible chance to drop these things:`,
            },
            {
              type: "list",
              data: `12 Popberry Seeds
                      6 Popberry Seeds
                      1 Mini Farmamix
                      1 Mini Farmamix Recipe
                      1 Fish Finder Five Hundred
                      5 Cooking Mix
                      1 Turd
                      1 Energy Drink
                      5 Cooking Mix
                      1 Infiniportable
                      1 Gathering Satchel`,
            },
          ],
        },
      ],
    },
    {
      id: "currencies",
      title: "Currencies",
      type: "section",
      content: [
        {
          type: "component",
          data: "Currencies",
        },
      ],
    },
    {
      id: "guilds",
      title: "Guilds",
      type: "section",
      content: [
        {
          type: "subdivision",
          id: "what-is-a-guild",
          title: "What is a Guild?",
          content: [
            {
              type: "text",
              data: `A guild is kinda like a group of people all aligned on the same gaming goals.<br/>
              Are you all grinders? Friends? Socializers? Money-makers? Things like that.. You want to find a guild where you fit right in.<br/>
              In Pixels, Guilds have a few benefits - Guilds can lock down lands so only their guild members can use the land, and that becomes very helpful when you're looking for hard-to-find industries.<br/>
              Also, guild competitions! They will be group events that you can only do with your guild. If you don't have one, well...you're going to miss out on that fun and reward.`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "do-i-need-guild",
          title: "Do I need to join one?",
          content: [
            {
              type: "text",
              data: `Nope~! You can play your entire adventure in Pixels without ever joining a guild. You could even make your own if you wanted to.`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "is-this-good-guild",
          title: "How do I know if I am joining a good guild?",
          content: [
            {
              type: "text",
              data: `There's never a guarantee. But you can ask around. Join their guild discord and see if their behaviour is something you align with. Always verify because you might be buying into something you definitely don't want to be a part of..`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "where-to-find-guild",
          title: "Where do I start looking?",
          content: [
            {
              type: "text",
              data: `Either the Pixels Discord, you can check in #guild-recruitment, or you can check the Guilds Page on the Dashboard, and search for something there. We have a <a href="../../lookup/guilds" rel="noreferrer" target="_blank" class="wiki-links">Guild Page</a> here as well so you can easily search for something that might fit your criteria ⸜(｡˃ ᵕ ˂ )⸝♡`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "cost-of-joining-guild",
          title: "What is the cost of joining a Guild?",
          content: [
            {
              type: "text",
              data: `That depends on the guild! Every guild would have a different price depending on how many shards they've sold. Guilds with more shards sold cost more. Less shards, lower price. Find something in yourrrr budget!`,
            },
          ],
        },
        {
          type: "subdivision",
          id: "how-to-make-guild",
          title: "What do I need to make one?",
          content: [
            {
              type: "text",
              data: `15 $PIXEL, and a name. This <a href="https://help.pixels.xyz/en/articles/9098771-creating-and-managing-a-guild" rel="noreferrer" target="_blank" class="wiki-links">guide</a> by Pixels does an okay job of explaining`,
            },
          ],
        },
      ],
    },
    {
      id: "durability",
      title: "Items with Durability",
      type: "section",
      content: [{
        type: "subdivision",
        id: "item-durability",
        title: "How Durable Are These Items?",
        content: [
        {
          type: "component",
          data: "Durability",
        },
      ],
    }
      ]
    }
  ],
};

export default starterGuideData;
